import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import type { Employee, EmployeesResponse, EmployeeFilters } from "@/types/api";
import { getEmployees } from "@/services/employeeService";

interface EmployeeContextState {
  employees: Employee[];
  includeTerminated: boolean;
  setIncludeTerminated: (value: boolean) => void;
  selectedEmployeeId: number | null;
  setSelectedEmployeeId: (employeeId: number | null) => void;
  selectedEmployee: Employee | null;
  loading: boolean;
  error: string | null;
  refresh: (options?: { userId?: string; filters?: EmployeeFilters }) => Promise<EmployeesResponse | void>;
  getEmployeeById: (employeeId: number) => Employee | undefined;
  activeUserContextKey: string; // 'current' or specific userId
}

const EmployeeContext = createContext<EmployeeContextState | undefined>(undefined);

export const EmployeeProvider: React.FC<{ children: React.ReactNode }>
  = ({ children }) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const urlUserId = searchParams.get("userId") || "current";

  const [includeTerminated, setIncludeTerminated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Cache employees and selected employee per user context key
  const [employeesByUser, setEmployeesByUser] = useState<Record<string, Employee[]>>({});
  const [selectedEmployeeIdByUser, setSelectedEmployeeIdByUser] = useState<Record<string, number | null>>({});

  const employees = employeesByUser[urlUserId] || [];
  const selectedEmployeeId = selectedEmployeeIdByUser[urlUserId] ?? null;
  const selectedEmployee = useMemo(() => {
    if (selectedEmployeeId == null) return null;
    return employees.find((e) => e.id === selectedEmployeeId) || null;
  }, [employees, selectedEmployeeId]);

  const getEmployeeById = useCallback((employeeId: number) => {
    return employees.find((e) => e.id === employeeId);
  }, [employees]);

  const refresh = useCallback(async (options?: { userId?: string; filters?: EmployeeFilters }) => {
    const effectiveUserId = options?.userId ?? urlUserId;
    const effectiveFilters: EmployeeFilters = {
      includeTerminated,
      ...(options?.filters || {}),
    };
    try {
      setLoading(true);
      setError(null);
      const data = await getEmployees({ userId: effectiveUserId !== "current" ? effectiveUserId : undefined, filters: effectiveFilters });
      setEmployeesByUser((prev) => ({ ...prev, [effectiveUserId]: data.employees }));
      // Maintain selection if still present; otherwise clear
      setSelectedEmployeeIdByUser((prev) => {
        const currentSelected = prev[effectiveUserId] ?? null;
        if (currentSelected != null && data.employees.some((e) => e.id === currentSelected)) {
          return prev;
        }
        return { ...prev, [effectiveUserId]: null };
      });
      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load employees";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [includeTerminated, urlUserId]);

  // Auto-load when user context key or includeTerminated changes
  useEffect(() => {
    void refresh();
  }, [urlUserId, includeTerminated, refresh]);

  const setSelectedEmployeeId = useCallback((employeeId: number | null) => {
    setSelectedEmployeeIdByUser((prev) => ({ ...prev, [urlUserId]: employeeId }));
  }, [urlUserId]);

  const value: EmployeeContextState = {
    employees,
    includeTerminated,
    setIncludeTerminated,
    selectedEmployeeId,
    setSelectedEmployeeId,
    selectedEmployee,
    loading,
    error,
    refresh,
    getEmployeeById,
    activeUserContextKey: urlUserId,
  };

  return (
    <EmployeeContext.Provider value={value}>{children}</EmployeeContext.Provider>
  );
};

export const useEmployees = (): EmployeeContextState => {
  const ctx = useContext(EmployeeContext);
  if (!ctx) {
    throw new Error("useEmployees must be used within an EmployeeProvider");
  }
  return ctx;
};




